"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

import { reportJsError } from "@/lib/js-error-beacon";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Label for where this boundary sits, recorded on the js_error beacon. */
  boundary?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Feed the owned js_error beacon (events_raw, rule 34 — edge only) so a React
    // render crash is queryable + joinable independent of Sentry. reportJsError
    // is fully self-contained (throttled, de-duped, never throws).
    reportJsError({
      error_message: error.message || error.name || "React render error",
      error_name: error.name || "Error",
      error_stack: error.stack || "",
      boundary: this.props.boundary ?? "react_error_boundary",
      component: info.componentStack ?? undefined,
    });
  }

  render() {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--ish-text-faint)" }}>
            Something went wrong. Please refresh the page.
          </div>
        )
      );
    }
    return this.props.children;
  }
}
